import { useCallback, useEffect, useState } from 'react'
import { LoadingScreen } from './LoadingScreen'
import { ScanLobby } from './ScanLobby'
import { CustomerSearchPanel } from './CustomerSearchPanel'
import { useFlow } from '../flow/FlowContext'
import { useSession } from '../session/SessionContext'
import { t } from '../i18n'
import { badgerConsumerReference, getLightspeedApi } from '../lightspeed/pos-api'
import { playSound } from '../sound'

/** Prompt to scan a customer's Badger card/QR.
 *
 * Three input paths, all funnelling into `lookup`:
 *   1. Native scanner / NFC (`pos_onBarcodeScanEvent` / `pos_onNFCEvent`, via `onCardScan`).
 *   2. Camera QR scan (`CameraScanner`) — for hosts without an integrated scanner (e.g. iPad);
 *      the LSK API can't open the camera, so this uses the web `getUserMedia` API directly.
 *   3. Manual entry — dev fallback / when scanning fails.
 */
export function IdleScreen() {
  const { dispatch } = useFlow()
  const { setScannedQr } = useSession()
  // The camera opens behind an explicit tap rather than auto-opening — that tap is the in-web-view
  // gesture iOS requires before any audio can play (see ScanLobby). The search panel beside it is
  // always available, so a customer with no phone is served without leaving this screen.
  const [scanning, setScanning] = useState(false)
  // Back camera by default (the cashier points the iPad's rear camera at the customer's QR);
  // "Change camera" flips it to the front.
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment')
  // While true, we're checking whether the open sale is already linked to a consumer (avoids a
  // flash of the camera before we relink). Starts true; flips false once the check finds no link.
  const [checkingLink, setCheckingLink] = useState(true)

  // Stable across renders (deps are the stable context setter + dispatch), so the card-scan
  // subscription below can depend on it honestly and still only subscribe once.
  const lookup = useCallback(
    (cardId: string) => {
      const id = cardId.trim()
      if (!id) return
      setScannedQr(id)
      dispatch({ type: 'SCAN' })
    },
    [setScannedQr, dispatch],
  )

  // A cashier-initiated identification — camera QR, native scanner, or picking someone from the
  // search panel. All three play the cue: it is the cashier's confirmation that the customer
  // landed, and it should not depend on WHICH way they identified them.
  //
  // Deliberately NOT used by the reopen relink below: that fires on its own when the extension is
  // reopened on an already-linked sale, and a chime nobody triggered reads as a phantom scan.
  const identify = useCallback(
    (cardId: string) => {
      playSound('scan')
      lookup(cardId)
    },
    [lookup],
  )

  // Reopen-without-rescan: if this open sale already carries a BADGER consumer external reference
  // (the cashier linked this customer in an earlier open of the same account), load that consumer
  // directly instead of asking for another scan. The reference persists on the account across POS
  // reopens, so this is the reliable relink signal. Falls through to scanning when there's no link.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const account = await getLightspeedApi().getCurrentAccount()
        const uid = badgerConsumerReference(account)
        if (!cancelled && uid) {
          lookup(uid)
          return
        }
      } catch {
        // ignore — fall through to the normal scan flow
      }
      if (!cancelled) setCheckingLink(false)
    })()
    return () => {
      cancelled = true
    }
  }, [lookup])

  // Native scanner / NFC.
  useEffect(() => getLightspeedApi().onCardScan(identify), [identify])

  if (checkingLink) {
    // Neutral label — we're checking whether this sale is already linked, NOT opening the camera.
    // (Showing "Starting camera…" here made the relink look like it launched the camera then
    // changed its mind.)
    return <LoadingScreen label={t('loading')} />
  }

  // One persistent two-pane layout (Figma 2464:2501): the camera opens INSIDE the left pane, so
  // the search panel stays put and the feed appears exactly where the cashier just tapped.
  return (
    <div className="flex h-full">
      <ScanLobby
        scanning={scanning}
        facingMode={facingMode}
        onStartScan={() => setScanning(true)}
        onFlipCamera={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))}
        onDecode={identify}
      />
      <CustomerSearchPanel onPickCustomer={identify} />
    </div>
  )
}
